import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';
import { Badge, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';

// Before/after photos the washer took (booking.photos, written by the mobile
// app). Each set is a WhatsApp-style album; click opens a viewer that pages through every
// photo on the order. Gallery picks are flagged with their original capture
// date so old photos stand out.
const ANGLE = { front: 'Front', right: 'Right', rear: 'Rear', left: 'Left', interior: 'Interior', extra: 'Extra' };
const AMBER = '#c77c02';
const fmt = (ms, f = 'DD MMM YYYY, hh:mm A') => moment(ms).tz('Asia/Dubai').format(f);

const ALBUM = 160; // px, square like a WhatsApp album bubble
const GAP = 2;

// Tile layout by count, WhatsApp-style: 1 full, 2 side by side, 3 = one tall
// + two stacked, 4+ = 2x2 with "+N" on the last tile.
const albumCells = (n) => {
    const half = (ALBUM - GAP) / 2;
    if (n === 1) return [{ w: ALBUM, h: ALBUM }];
    if (n === 2) return [{ w: half, h: ALBUM }, { w: half, h: ALBUM }];
    if (n === 3) return [{ w: half, h: ALBUM }, { w: half, h: half }, { w: half, h: half }];
    return [0, 1, 2, 3].map(() => ({ w: half, h: half }));
};

const Album = ({ items, offset, onOpen }) => {
    const cells = albumCells(items.length);
    const extra = items.length - cells.length;
    const tile = (photo, i, cell) => (
        <div
            key={photo.path || photo.url}
            onClick={() => onOpen(offset + i)}
            title={ANGLE[photo.angle] || photo.angle}
            style={{ position: 'relative', width: cell.w, height: cell.h, cursor: 'pointer', overflow: 'hidden' }}>
            <img alt={photo.angle} src={photo.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            {i === cells.length - 1 && extra > 0 ? (
                <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 600,
                }}>
                    +{extra}
                </div>
            ) : null}
        </div>
    );
    return (
        <div style={{
            width: ALBUM, height: ALBUM, borderRadius: 10, overflow: 'hidden',
            display: 'flex', flexWrap: 'wrap',
            gap: GAP, background: '#e2eaf2',
        }}>
            {items.length === 3 ? (
                <>
                    {tile(items[0], 0, cells[0])}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
                        {tile(items[1], 1, cells[1])}
                        {tile(items[2], 2, cells[2])}
                    </div>
                </>
            ) : (
                items.slice(0, cells.length).map((photo, i) => tile(photo, i, cells[i]))
            )}
        </div>
    );
};

const Group = ({ title, items, skipped, offset, onOpen }) => {
    const galleryCount = items.filter((p) => p.source === 'gallery').length;
    const last = items.reduce((max, p) => Math.max(max, p.takenAt || 0), 0);
    return (
        <div style={{ flex: '0 0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span className="font-weight-bold">{title}</span>
                <span style={{ fontSize: 12, color: '#637385' }}>
                    {items.length ? `${items.length} photo${items.length > 1 ? 's' : ''}` : ''}
                </span>
                {galleryCount ? <Badge color="warning" pill>{galleryCount} from gallery</Badge> : null}
            </div>
            {items.length ? (
                <>
                    <Album items={items} offset={offset} onOpen={onOpen} />
                    <div style={{ fontSize: 11, color: '#637385', marginTop: 4 }}>{fmt(last, 'DD MMM, hh:mm A')}</div>
                </>
            ) : (
                <div style={{
                    width: ALBUM, height: 56, borderRadius: 10, border: '1px dashed #d9e3ed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <span className="text-semi-muted">{skipped ? 'Skipped by washer' : 'No photos'}</span>
                </div>
            )}
        </div>
    );
};

const Viewer = ({ photos, index, onChange, onClose }) => {
    const photo = photos[index];
    const prev = useCallback(() => onChange((index - 1 + photos.length) % photos.length), [index, photos.length, onChange]);
    const next = useCallback(() => onChange((index + 1) % photos.length), [index, photos.length, onChange]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') prev();
            if (e.key === 'ArrowRight') next();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [prev, next]);

    const arrow = (side, onClick) => (
        <button
            type="button"
            onClick={onClick}
            aria-label={side === 'left' ? 'Previous photo' : 'Next photo'}
            style={{
                position: 'absolute', top: '50%', [side]: 12, transform: 'translateY(-50%)',
                width: 40, height: 40, borderRadius: 20, border: 'none',
                background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: 18,
            }}>
            <i className={side === 'left' ? 'simple-icon-arrow-left' : 'simple-icon-arrow-right'} />
        </button>
    );

    return (
        // Order details is itself a modal (z-index 1050); stack above it.
        <Modal isOpen toggle={onClose} size="lg" centered zIndex={1070}>
            <ModalHeader toggle={onClose}>
                {photo.set === 'before' ? 'Before' : 'After'} · {ANGLE[photo.angle] || photo.angle}
                <span className="text-semi-muted" style={{ fontSize: 13, marginLeft: 8 }}>
                    {index + 1} of {photos.length}
                </span>
            </ModalHeader>
            <ModalBody>
                <div style={{ position: 'relative', background: '#111', borderRadius: 8, textAlign: 'center' }}>
                    <img
                        alt={photo.angle}
                        src={photo.url}
                        style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
                    />
                    {photos.length > 1 ? arrow('left', prev) : null}
                    {photos.length > 1 ? arrow('right', next) : null}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, marginTop: 12 }}>
                    <div style={{ fontSize: 13 }}>
                        <div>Uploaded {fmt(photo.takenAt)}</div>
                        {photo.source === 'gallery' ? (
                            <div style={{ color: AMBER }}>
                                From the washer's gallery{photo.capturedAt ? ` · originally taken ${fmt(photo.capturedAt)}` : ''}
                            </div>
                        ) : (
                            <div className="text-semi-muted">Taken with the in-app camera</div>
                        )}
                    </div>
                    <a href={photo.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                        Open original <i className="simple-icon-share-alt" />
                    </a>
                </div>

                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginTop: 12, paddingBottom: 4 }}>
                    {photos.map((p, i) => (
                        <img
                            key={p.path || p.url}
                            alt={p.angle}
                            src={p.url}
                            onClick={() => onChange(i)}
                            title={`${p.set === 'before' ? 'Before' : 'After'} · ${ANGLE[p.angle] || p.angle}`}
                            style={{
                                width: 52, height: 52, flex: '0 0 auto', objectFit: 'cover', borderRadius: 6, cursor: 'pointer',
                                opacity: i === index ? 1 : 0.55,
                                border: i === index ? '2px solid #008CFF' : '2px solid transparent',
                            }}
                        />
                    ))}
                </div>
            </ModalBody>
        </Modal>
    );
};

const OrderPhotos = ({ photos }) => {
    const [openIndex, setOpenIndex] = useState(null);
    if (!photos) return null;

    if (photos.purgedAt) {
        return (
            <>
                <Label className="mb-0 text-semi-muted">Before / After Photos:</Label>
                <p className="font-weight-bold">Removed after retention on {fmt(photos.purgedAt, 'DD MMM YYYY')}</p>
            </>
        );
    }

    const before = (photos.before || []).map((p) => ({ ...p, set: 'before' }));
    const after = (photos.after || []).map((p) => ({ ...p, set: 'after' }));
    const all = [...before, ...after];

    return (
        <div style={{ marginBottom: 16 }}>
            <Label className="mb-2 text-semi-muted">Before / After Photos:</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                <Group title="Before" items={before} skipped={photos.beforeSkipped} offset={0} onOpen={setOpenIndex} />
                <Group title="After" items={after} skipped={photos.afterSkipped} offset={before.length} onOpen={setOpenIndex} />
            </div>
            {photos.beforeNote ? (
                <p style={{ marginTop: 10, marginBottom: 0 }}>
                    <span className="text-semi-muted">Condition noted by washer: </span>
                    <span className="font-weight-bold">{photos.beforeNote}</span>
                </p>
            ) : null}
            {openIndex !== null && all[openIndex] ? (
                <Viewer photos={all} index={openIndex} onChange={setOpenIndex} onClose={() => setOpenIndex(null)} />
            ) : null}
        </div>
    );
};

export default OrderPhotos;
